import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Wand2, Loader2, Star, RotateCcw, Undo2, Upload, Info, Settings2, ImageOff, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import ComparisonSlider from "@/components/ComparisonSlider";
import { PhotoAddButtons, UploadQueue } from "@/components/PhotoUploader";
import { api, apiError } from "@/lib/api";
import { usePhotoUpload } from "@/hooks/use-photo-upload";
import { FOTOBOOTH_TEMPLATES } from "@/lib/constants";

const isEditable = (img) => (img.original_url || img.url || "").startsWith("data:");

// Slot-Kategorie -> Verarbeitungsart (muss Backend-Logik entsprechen)
const CATEGORY_TYPE = { exterieur: "aussen", interieur: "innen", technik: "innen", dokumente: "innen", extras: "innen", zustand: "innen" };
const TYPE_META = {
  aussen: { label: "Außen → Studio", chip: "bg-sky-500/15 text-sky-300 border-sky-500/30", action: "Studio-Look anwenden", badge: "STUDIO" },
  innen: { label: "Innen → Optimierung", chip: "bg-violet-400/15 text-violet-300 border-violet-400/30", action: "Schonend optimieren", badge: "OPTIMIERT" },
  auto: { label: "Auto-Erkennung", chip: "bg-slate-500/20 text-slate-300 border-slate-500/30", action: "Automatisch bearbeiten", badge: "KI" },
};

export default function FotoboothPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const vehicleId = searchParams.get("vehicle") || "";

  const [vehicles, setVehicles] = useState(null);
  const [photos, setPhotos] = useState(null);
  const [branding, setBranding] = useState(null);
  const [template, setTemplate] = useState("studio_hell");
  const [blurPlate, setBlurPlate] = useState(true);
  const [applyLogo, setApplyLogo] = useState(false);
  const [windowsMode, setWindowsMode] = useState(true); // Studio-Hintergrund durch Scheiben fortsetzen
  const [processing, setProcessing] = useState({});
  const [batch, setBatch] = useState(null); // {done, total}
  const [slotCats, setSlotCats] = useState({});

  const imageType = (img) => (img.slot && slotCats[img.slot] ? (CATEGORY_TYPE[slotCats[img.slot]] || "aussen") : "auto");

  useEffect(() => {
    api.get("/vehicles").then((r) => setVehicles(r.data)).catch(() => setVehicles([]));
    api.get("/meta").then((r) => {
      const map = {};
      (r.data.photo_slots || []).forEach((s) => { map[s.key] = s.category; });
      setSlotCats(map);
    }).catch(() => {});
    api.get("/settings/branding").then((r) => {
      setBranding(r.data);
      if (r.data.fotobooth_hintergrund) setTemplate(r.data.fotobooth_hintergrund);
      setApplyLogo(Boolean(r.data.wasserzeichen_aktiv && r.data.logo_url));
    }).catch(() => setBranding({}));
  }, []);

  const loadPhotos = useCallback(() => {
    if (!vehicleId) { setPhotos(null); return; }
    api.get(`/vehicles/${vehicleId}/photos`).then((r) => setPhotos(r.data)).catch(() => setPhotos(null));
  }, [vehicleId]);
  useEffect(() => { loadPhotos(); }, [loadPhotos]);

  const allImages = useMemo(() => {
    if (!photos) return [];
    const slotImgs = photos.slots.flatMap((s) => s.images.map((img) => ({ ...img, slotLabel: s.label })));
    const free = (photos.free_images || []).map((img) => ({ ...img, slotLabel: "Weitere" }));
    return [...slotImgs, ...free];
  }, [photos]);

  const vehicle = useMemo(() => (vehicles || []).find((v) => v.id === vehicleId), [vehicles, vehicleId]);

  const processOne = async (img, { silent = false } = {}) => {
    setProcessing((p) => ({ ...p, [img.id]: true }));
    try {
      const r = await api.post(`/vehicles/${vehicleId}/photos/${img.id}/fotobooth`, {
        template, blur_plate: blurPlate, apply_branding: applyLogo, fenster_fortsetzen: windowsMode,
      });
      if (!silent) toast.success(`Studio-Look erstellt (${(r.data.duration_ms / 1000).toFixed(1)}s): ${r.data.steps.join(" · ")}`, { duration: 5000 });
      return true;
    } catch (e) {
      if (!silent) toast.error(apiError(e, "Bildbearbeitung fehlgeschlagen – Original bleibt erhalten"));
      return false;
    } finally {
      setProcessing((p) => ({ ...p, [img.id]: false }));
      loadPhotos();
    }
  };

  // Alle Fotos AUF EINMAL durch die Fotobooth (parallel, max. 4 gleichzeitig)
  const processAll = async () => {
    const editable = allImages.filter((img) => isEditable(img));
    if (editable.length === 0) {
      toast.info("Keine bearbeitbaren Fotos vorhanden. Bitte eigene Fotos hochladen (Demo-Bilder von Bild-URLs können nicht bearbeitet werden).");
      return;
    }
    setBatch({ done: 0, total: editable.length });
    // Alle Karten sofort in den Verarbeitungszustand versetzen
    setProcessing((p) => {
      const next = { ...p };
      editable.forEach((img) => { next[img.id] = true; });
      return next;
    });

    let ok = 0;
    let unsure = 0;
    let done = 0;
    const CONCURRENCY = 4;
    const queue = [...editable];

    const worker = async () => {
      while (queue.length > 0) {
        const img = queue.shift();
        const res = await processOne(img, { silent: true });
        if (res) {
          ok++;
          if (res.image?.windows_status === "unsicher") unsure++;
        }
        done++;
        setBatch({ done, total: editable.length });
      }
    };

    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, editable.length) }, worker));
    setBatch(null);
    loadPhotos();
    toast.success(`Fotobooth abgeschlossen: ${ok} von ${editable.length} Bildern in Studio-Qualität`);
    if (unsure > 0) {
      toast.warning(`${unsure} Bild(er) mit unsicherem Scheiben-Ergebnis – bitte prüfen. Das Original bleibt jederzeit über „Original verwenden“ verfügbar.`, { duration: 9000 });
    }
  };

  const revertToOriginal = async (img) => {
    try {
      await api.post(`/vehicles/${vehicleId}/photos/${img.id}/use-original`);
      toast.success("Original wird verwendet (bearbeitete Version bleibt gespeichert)");
      loadPhotos();
    } catch (e) { toast.error(apiError(e)); }
  };

  const setCover = async (img) => {
    await api.post(`/vehicles/${vehicleId}/photos/${img.id}/cover`);
    toast.success("Titelbild gesetzt");
    loadPhotos();
  };

  // Upload-Queue: Fortschritt pro Bild, Einzel-Retry, HEIC-Unterstützung.
  // Neue Bilder landen unter "Weitere" (keine falschen Perspektiven behaupten).
  const uploader = usePhotoUpload(vehicleId, { onUploaded: () => loadPhotos() });

  const anyProcessing = Object.values(processing).some(Boolean) || Boolean(batch);

  return (
    <div className="space-y-5 pb-10">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl border border-primary/25 bg-card p-5 lg:p-6" data-tour="fotobooth-hero">
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(700px 260px at 10% 0%, rgba(56,189,248,0.14), transparent 70%)" }} />
        <div className="relative flex flex-wrap items-center justify-between gap-4">
          <div className="max-w-2xl">
            <h1 className="font-display text-2xl lg:text-3xl font-bold tracking-[-0.02em] flex items-center gap-2">
              <Wand2 className="h-6 w-6 text-primary" /> Fotobooth
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Parkplatzfoto rein – professionelles Studio-Bild raus. Die KI ersetzt nur Hintergrund und Bildqualität.
              <span className="text-foreground/80"> Das Fahrzeug selbst bleibt immer 100% unverändert.</span>
            </p>
          </div>
          <div className="w-full sm:w-[300px]">
            <Label className="text-xs text-muted-foreground">Fahrzeug</Label>
            <Select value={vehicleId || undefined} onValueChange={(v) => setSearchParams({ vehicle: v })}>
              <SelectTrigger className="mt-1" data-testid="fotobooth-vehicle-select">
                <SelectValue placeholder="Fahrzeug auswählen..." />
              </SelectTrigger>
              <SelectContent>
                {(vehicles || []).map((v) => (
                  <SelectItem key={v.id} value={v.id}>{v.marke} {v.modell} · {v.bestandsnummer}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {!vehicleId ? (
        <Card>
          <CardContent className="py-14 text-center space-y-3" data-testid="fotobooth-empty-state">
            <Sparkles className="h-8 w-8 text-primary mx-auto" />
            <p className="text-sm text-muted-foreground">Wählen Sie oben ein Fahrzeug aus, um dessen Fotos in Studio-Qualität zu verwandeln.</p>
          </CardContent>
        </Card>
      ) : !photos ? (
        <div className="grid lg:grid-cols-12 gap-4">
          <Skeleton className="lg:col-span-4 h-72" />
          <Skeleton className="lg:col-span-8 h-72" />
        </div>
      ) : (
        <div className="grid lg:grid-cols-12 gap-4">
          {/* Einstellungen */}
          <div className="lg:col-span-4 space-y-4">
            <Card data-testid="fotobooth-settings-card">
              <CardHeader className="pb-2"><CardTitle className="text-base font-display flex items-center gap-2"><Settings2 className="h-4 w-4 text-primary" /> Hintergrund & Optionen</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-2">
                  {FOTOBOOTH_TEMPLATES.map((tpl) => (
                    <button
                      key={tpl.key}
                      onClick={() => setTemplate(tpl.key)}
                      className={`rounded-xl border p-2 text-left transition-colors ${template === tpl.key ? "border-primary ring-1 ring-primary/50" : "border-border hover:border-primary/40"}`}
                      data-testid={`fotobooth-template-${tpl.key}`}
                    >
                      <div className="h-10 w-full rounded-lg" style={{ background: tpl.swatch }} />
                      <div className="text-xs font-medium mt-1.5">{tpl.label}</div>
                      <div className="text-[10px] text-muted-foreground">{tpl.hint}</div>
                    </button>
                  ))}
                </div>

                <div className="flex items-center justify-between gap-3">
                  <Label htmlFor="blur-plate" className="text-sm">Kennzeichen unkenntlich machen</Label>
                  <Switch id="blur-plate" checked={blurPlate} onCheckedChange={setBlurPlate} data-testid="fotobooth-blur-plate-switch" />
                </div>
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <Label htmlFor="windows-mode" className="text-sm">Studio-Hintergrund durch Scheiben fortsetzen</Label>
                    <p className="text-[10px] text-muted-foreground">Ersetzt auch den alten Aufnahmeort hinter Front-, Seiten- und Heckscheiben. Innenraum, Tönung & Reflexionen bleiben erhalten.</p>
                  </div>
                  <Switch id="windows-mode" checked={windowsMode} onCheckedChange={setWindowsMode} data-testid="fotobooth-windows-switch" />
                </div>
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <Label htmlFor="apply-logo" className="text-sm">Händlerlogo einfügen</Label>
                    {!branding?.logo_url && (
                      <p className="text-[10px] text-muted-foreground">
                        Kein Logo hinterlegt – <Link to="/app/einstellungen" className="text-primary underline">in den Einstellungen hochladen</Link>
                      </p>
                    )}
                  </div>
                  <Switch id="apply-logo" checked={applyLogo} onCheckedChange={setApplyLogo} disabled={!branding?.logo_url} data-testid="fotobooth-logo-switch" />
                </div>

                <Button className="w-full h-11 gap-2 font-semibold" onClick={processAll} disabled={anyProcessing || uploader.active} data-testid="fotobooth-apply-all-button">
                  {batch ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
                  {batch ? `${batch.done} von ${batch.total} fertig...` : uploader.active ? "Upload läuft..." : "Auf alle Fotos anwenden"}
                </Button>
                {batch && <Progress value={(batch.done / batch.total) * 100} className="h-1.5" />}

                <div className="rounded-lg border border-border bg-secondary/30 p-2.5 text-[11px] text-muted-foreground flex gap-2">
                  <Info className="h-3.5 w-3.5 shrink-0 mt-0.5 text-sky-300" />
                  Echte KI-Bildbearbeitung (ca. 10–20 Sek. pro Bild). Außenaufnahmen erhalten den Studio-Hintergrund – auf Wunsch auch durch die Fahrzeugscheiben, damit der alte Aufnahmeort nicht mehr durchs Glas sichtbar ist. Innenraum- & Detailfotos werden automatisch nur schonend optimiert. Niemals verändert: Schäden, Farbe, Felgen, Ausstattung, Innenraum.
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-4 space-y-3">
                <div className="text-sm font-medium">Fotos hinzufügen</div>
                <PhotoAddButtons onFiles={(files) => uploader.addFiles(files, null)} testPrefix="fotobooth-photo-add" />
                <UploadQueue items={uploader.items} onRetry={uploader.retry} onDismiss={uploader.dismiss} testPrefix="fotobooth-photo-queue" />
              </CardContent>
            </Card>
          </div>

          {/* Bilder */}
          <div className="lg:col-span-8">
            {allImages.length === 0 ? (
              <Card>
                <CardContent className="py-14 text-center space-y-3" data-testid="fotobooth-no-photos">
                  <ImageOff className="h-8 w-8 text-muted-foreground mx-auto" />
                  <p className="text-sm text-muted-foreground">Noch keine Fotos für dieses Fahrzeug. Laden Sie links Fotos hoch oder nutzen Sie den geführten Foto-Workflow.</p>
                  <Button variant="outline" onClick={() => navigate(`/app/fahrzeuge/${vehicleId}?tab=photos`)} data-testid="fotobooth-goto-photos">Zum Foto-Workflow</Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid sm:grid-cols-2 gap-4" data-testid="fotobooth-grid">
                {allImages.map((img) => {
                  const busy = processing[img.id];
                  const hasResult = img.processed_url && img.processing_status === "processed";
                  const editable = isEditable(img);
                  const t = TYPE_META[imageType(img)];
                  const resultBadge = img.processing_mode === "optimiert" ? "OPTIMIERT" : img.processing_mode === "auto" ? "KI-BEARBEITET" : "STUDIO";
                  return (
                    <Card key={img.id} className="overflow-hidden" data-testid={`fotobooth-image-${img.id}`}>
                      <div className="relative">
                        {hasResult ? (
                          <ComparisonSlider beforeSrc={img.original_url || img.url} afterSrc={img.processed_url} testId={`fotobooth-slider-${img.id}`} className="rounded-none border-0" />
                        ) : (
                          <img src={img.url} alt={img.slotLabel} className="w-full aspect-[4/3] object-cover" loading="lazy" />
                        )}
                        {busy && (
                          <div className="absolute inset-0 bg-black/60 backdrop-blur-[2px] flex flex-col items-center justify-center gap-2" data-testid={`fotobooth-processing-${img.id}`}>
                            <Loader2 className="h-6 w-6 animate-spin text-primary" />
                            <span className="text-xs text-white">{imageType(img) === "innen" ? "KI optimiert schonend..." : "KI erstellt Studio-Look..."}</span>
                          </div>
                        )}
                        {img.is_cover && <span className="absolute bottom-2 left-2 rounded-full bg-primary text-primary-foreground text-[9px] font-bold px-1.5 py-0.5">TITELBILD</span>}
                        {hasResult && !busy && <span className="absolute bottom-2 right-2 rounded-full bg-emerald-500/90 text-black text-[9px] font-bold px-1.5 py-0.5">{resultBadge}</span>}
                        {img.processing_status === "error" && !busy && (
                          <span className="absolute bottom-2 right-2 rounded-full bg-red-500/90 text-white text-[9px] font-bold px-1.5 py-0.5">FEHLER – erneut versuchen</span>
                        )}
                      </div>
                      <CardContent className="py-2.5 px-3">
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-1.5 min-w-0">
                            <span className="text-xs text-muted-foreground truncate">{img.slotLabel}</span>
                            <span className={`shrink-0 rounded-full border px-1.5 py-0.5 text-[9px] font-semibold ${t.chip}`} data-testid={`fotobooth-type-${img.id}`}>{t.label}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button variant="ghost" size="icon" className="h-7 w-7" title="Als Titelbild" onClick={() => setCover(img)} data-testid={`fotobooth-cover-${img.id}`}>
                              <Star className={`h-3.5 w-3.5 ${img.is_cover ? "text-amber-300" : ""}`} />
                            </Button>
                            {hasResult && (
                              <>
                                <Button variant="ghost" size="icon" className="h-7 w-7" title="Original verwenden" onClick={() => revertToOriginal(img)} data-testid={`fotobooth-original-${img.id}`}>
                                  <Undo2 className="h-3.5 w-3.5" />
                                </Button>
                                <Button variant="ghost" size="icon" className="h-7 w-7" title="Neu generieren" disabled={busy} onClick={() => processOne(img)} data-testid={`fotobooth-regenerate-${img.id}`}>
                                  <RotateCcw className="h-3.5 w-3.5" />
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                        {!hasResult && (
                          editable ? (
                            <Button size="sm" className="w-full mt-2 gap-1.5 font-semibold" disabled={busy || anyProcessing} onClick={() => processOne(img)} data-testid={`fotobooth-process-${img.id}`}>
                              <Wand2 className="h-3.5 w-3.5" /> {t.action}
                            </Button>
                          ) : (
                            <p className="text-[10px] text-muted-foreground mt-2" data-testid={`fotobooth-not-editable-${img.id}`}>
                              Demo-Bild (externe URL) – nur eigene Foto-Uploads können bearbeitet werden.
                            </p>
                          )
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
